import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import clsx from 'clsx';
import { useParams } from 'react-router-dom';

import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';

import InsertLink from '../InsertLink/InsertLink';
import { EditReportModal } from './EditReportModal';

import { useCreateDashboardReport } from '../../hooks';
import { showToast } from '@/src/globalFunction';
import { useUpdateDashboardReport } from '../../hooks/useUpdateDashboardReport';
import { useReorderDashboardReports } from '../../hooks/useReorderDashboardReports';
import { useDeleteDashboardReport } from '../../hooks/useDeleteDashboardReport';

type Report = { name: string; url: string; id?: string };

function arrayMove(arr: Report[], from: number, to: number) {
  const updated = [...arr];
  const [removed] = updated.splice(from, 1);
  updated.splice(to, 0, removed);
  return updated;
}

export const ReportUrlList = ({
  reportUrls,
  setReportUrls,
  refetchReports,
}: {
  reportUrls: Report[];
  setReportUrls: (reports: Report[]) => void;
  refetchReports?: () => Promise<Report[]>;
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();

  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const reorderedRef = useRef<Report[] | null>(null);
  const [editModalIdx, setEditModalIdx] = useState<number | null>(null);
  const [editModalName, setEditModalName] = useState('');
  const [editModalUrl, setEditModalUrl] = useState('');
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const deleteReportMutation = useDeleteDashboardReport(projectId, editingReportId || '');

  const updateReportMutation = useUpdateDashboardReport(projectId, editingReportId || '');
  const reorderMutation = useReorderDashboardReports(projectId);

  const handleEditModal = (idx: number) => {
    setEditModalIdx(idx);
    setEditModalName(reportUrls[idx].name || '');
    setEditModalUrl(reportUrls[idx].url || '');
    setEditingReportId(reportUrls[idx]?.id || null);
  };

  const handleEditSave = async (name: string, url: string) => {
    if (editModalIdx !== null) {
      const report = reportUrls[editModalIdx];
      let newReports = [];
      if (report && report.id) {
        try {
          await updateReportMutation.editDashboardReportUrlAsync({
            name: name?.trim() || '',
            url: url?.trim() || '',
          });
          showToast('success', 'Report successfully updated.');
          if (refetchReports) {
            newReports = await refetchReports();
            setReportUrls(newReports);
          }
        } catch (e) {
          showToast('error', "There's a problem updating this report.");
          console.error(e);
        }
      } else {
        const updated = [...reportUrls];
        updated[editModalIdx] = { ...report, name: name?.trim() || '', url: url?.trim() || '' };
        newReports = updated;
        setReportUrls(updated);
      }
      // Update Redux store with new lookerReports
      if (newReports && newReports.length > 0) {
        dispatch({
          type: 'SET_PROJECT_SELECTED',
          Selected: {
            lookerReports: newReports,
          },
        });
      }
    }
    setEditModalIdx(null);
    setEditModalName('');
    setEditModalUrl('');
    setEditingReportId(null);
  };

  const handleDelete = async () => {
    if (editModalIdx !== null) {
      const report = reportUrls[editModalIdx];
      let newReports = [];
      if (report && report.id && projectId) {
        try {
          await deleteReportMutation.mutateAsync({ name: report.name, url: report.url });
          showToast('success', 'Report successfully deleted.');
        } catch (e) {
          showToast('error', "There's a problem deleting this report.");
          console.log(e);
        }
      }
      if (refetchReports) {
        newReports = await refetchReports();
        setReportUrls(newReports);
      } else {
        newReports = reportUrls.filter((_, i) => i !== editModalIdx);
        setReportUrls(newReports);
      }
      // Update Redux store with new lookerReports and clear single report URL fields
      dispatch({
        type: 'SET_PROJECT_SELECTED',
        Selected: {
          lookerReports: newReports,
          lookerReportUrl: undefined,
        },
      });
    }
    setEditModalIdx(null);
    setEditModalName('');
    setEditModalUrl('');
    setEditingReportId(null);
  };

  const handleDragStart = (idx: number) => setDraggedIdx(idx);
  // Track the last drop index for correct ordering
  const [dropIdx, setDropIdx] = useState<number | null>(null);

  const handleDragOver = (idx: number) => {
    if (draggedIdx === null || draggedIdx === idx) return;
    // Use the latest array for reorder
    const latest = reorderedRef.current || reportUrls;
    const moved = arrayMove(latest, draggedIdx, idx);
    reorderedRef.current = moved;
    setReportUrls(moved);
    setDropIdx(idx);
    setDraggedIdx(idx);
  };

  const handleDragEnd = async () => {
    if (draggedIdx !== null && dropIdx !== null) {
      const reordered = reorderedRef.current || reportUrls;
      const reorderedIds = reordered.map((r) => r?.id).filter(Boolean);
      if (reorderedIds.length === reportUrls.length) {
        try {
          const result = await reorderMutation.reorderDashboardReportsAsync({
            'report-ids': reorderedIds,
          });
          // Always refetch after reorder to sync UI with backend
          if (refetchReports) {
            const reports = await refetchReports();
            setReportUrls(reports);
          }
        } catch (e) {
          // Optionally handle error
        }
      }
    }
    setDraggedIdx(null);
    setDropIdx(null);
    reorderedRef.current = null;
  };

  return (
    <div className={clsx('tw-flex', 'tw-flex-col', 'tw-gap-0', 'tw-w-full')}>
      {/* Header Row */}
      <div
        className={clsx(
          'tw-grid',
          'tw-grid-cols-12',
          'tw-gap-2',
          'tw-px-2',
          'tw-py-2',
          'tw-bg-cfo-grey-100',
          'tw-border-b',
          'tw-font-brandonTextMedium',
          'tw-text-[14px]',
          'tw-normal-case',
          'tw-tracking-normal',
          'tw-text-cfo-black',
          'tw-border',
          'tw-border-[#EEEEEE]'
        )}
      >
        <div className="tw-col-span-5 ">Report Name</div>
        <div className="tw-col-span-5 tw-justify-around tw-flex">URL</div>
        <div className="tw-col-span-2 tw-text-center">Edit Report</div>
      </div>
      {reportUrls.length === 0 && (
        <div className={clsx('tw-text-gray-400', 'tw-py-2')}>No reports added.</div>
      )}
      <div className={clsx('tw-flex', 'tw-flex-col', 'tw-gap-0', 'tw-w-full')}>
        {reportUrls.map((report, idx) => (
          <div
            key={idx}
            className={clsx(
              'tw-grid tw-grid-cols-12 tw-items-center tw-gap-2 tw-bg-white tw-p-3 tw-rounded tw-shadow-sm tw-border tw-border-gray-200',
              draggedIdx === idx && 'tw-bg-cfo-blue-50',
              idx !== reportUrls.length - 1 && 'tw-mb-2'
            )}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={(e) => {
              e.preventDefault();
              handleDragOver(idx);
            }}
            onDragEnd={handleDragEnd}
            style={{ minHeight: 48 }}
          >
            {/* Drag handle */}
            <span
              className={clsx(
                'tw-cursor-move',
                'tw-px-2',
                'tw-text-gray-400',
                'hover:tw-text-cfo-blue-600',
                'tw-col-span-1'
              )}
              title="Drag to reorder"
            >
              <Icon icon={'drag_indicator'} />
            </span>
            {(() => {
              const name = report.name || '';
              const isLong = name.length > 150;
              const displayed = isLong ? name.slice(0, 150) + '…' : name;

              const longStyle: React.CSSProperties = {
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              };

              const shortStyle: React.CSSProperties = {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              };

              return (
                <span
                  className={clsx('tw-col-span-4')}
                  title={name}
                  style={isLong ? longStyle : shortStyle}
                >
                  {name ? displayed : <span className="tw-text-gray-400">(empty)</span>}
                </span>
              );
            })()}
            {/* URL Icon Button */}
            <div
              className={clsx('tw-col-span-5', 'tw-flex', 'tw-items-center', 'tw-justify-around')}
            >
              {(() => {
                const urlTrimmed = (report.url || '').trim();
                const isUrl = !!urlTrimmed;
                return (
                  <a
                    href={isUrl ? urlTrimmed : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={clsx(
                      'tw-inline-flex',
                      'tw-items-center',
                      'tw-justify-center',
                      'tw-rounded-full',
                      'tw-transition-colors',
                      isUrl
                        ? 'tw-bg-cfo-blue-600 hover:tw-bg-cfo-blue-700 tw-text-white'
                        : 'tw-bg-transparent tw-text-gray-300 tw-border tw-border-black tw-border-dashed',
                      'tw-w-12',
                      'tw-h-12'
                    )}
                    style={{ minWidth: 0 }}
                    title={isUrl ? urlTrimmed : 'No URL'}
                    tabIndex={isUrl ? 0 : -1}
                    aria-disabled={!isUrl}
                  >
                    <Icon icon={ICONS.link} />
                  </a>
                );
              })()}
            </div>
            {/* Edit button */}
            <div className={clsx('tw-col-span-2', 'tw-flex', 'tw-justify-center')}>
              <button
                type="button"
                onClick={() => handleEditModal(idx)}
                className={clsx(
                  'tw-text-cfo-blue-600',
                  'tw-px-2',
                  'tw-py-1',
                  'tw-rounded',
                  'hover:tw-bg-cfo-blue-50'
                )}
                title="Edit Embedded Report"
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Edit Modal */}
      {editModalIdx !== null && (
        <EditReportModal
          showModal={editModalIdx !== null}
          toggleShowModal={() => setEditModalIdx(null)}
          defaultName={editModalName}
          defaultUrl={editModalUrl}
          onSave={handleEditSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};
